import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, InlineAlertType, InlineAlertWidget, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, RadioLayoutType, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { MapDispatchToProps, MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { ExtractEncryptionMode } from '~src/commonInfra/utils/sites/SitePropertyTypes';
import { rotateSiteEncryptionKey } from '~src/kmsInOnline/services/RotateSiteEncryptionKeyService';
import { IGetSiteEncryptionKeyResult } from '~src/settings/security-settings/sections/extract-encryption/models/ExtractEncryptionModels';
import { IUpdateSiteSettingsForSiteAdminResult, UpdateSiteSettingsForSiteAdminRequest } from '~src/settings/ServerApi/requests/siteSettings/UpdateSiteSettingsForSiteAdminRequest';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { getExtractEncryptionKeyRequest } from './actions/GetExtractEncryptionKeyRequestAction';
import { getSiteSettingsRequest, siteSettingsRequestSucceeded } from './actions/SiteSettingsRequestAction';
import { CMEKEncryptionKeyTable } from './components/CMEKEncryptionKeyTable';
import { KMSEncryptionKeyTable } from './components/KMSEncryptionKeyTable';
import { openGenerateKMSKeyDialog } from './dialogs/KMSKeyGenerateDialog';
import { openKMSKeyMigrationDialog } from './dialogs/KMSKeyMigrationDialog';
import { openReplaceActiveKMSKeyDialog } from './dialogs/KMSKeyReplaceActiveDialog';
import { updateSiteSettings } from './services/UpdateSiteSettingsService';
import { extractEncryptionSettingsContainer, extractEncryptionSpaceContainer } from './styles/ExtractEncryptionSettingsStyles';
import { getSetEncryptionModeEnforcedRequest } from './utils/ExtractEncrpytionUtils';


export interface ExtractEncryptionSectionDispatchProps {
  retrieveExtractEncryptionKey: () => void;
  retrieveSiteSettings: (siteId: string) => void;
  updateSiteSettings: (siteSettings: ISiteSettingsForSiteAdmin) => void;
}

export interface ExtractEncryptionSectionStateProps {
  readonly isLoading: boolean;
  readonly encryptionKey: IGetSiteEncryptionKeyResult;
  readonly siteSettings: ISiteSettingsForSiteAdmin;
}

interface ExtractEncryptionSectionOwnProps {
  siteId: string;
}

export enum EncryptionType {
  CMEK = 0,
  KMS = 1
}

export interface ExtractEncryptionSectionState {
  isChecked: boolean;
  isRotating: boolean;
  isSavingSettings: boolean;
  selectedEncryptionType: EncryptionType;
  readonly hasError: boolean;
}

export type ExtractEncryptionSectionProps = ExtractEncryptionSectionOwnProps & ExtractEncryptionSectionDispatchProps & ExtractEncryptionSectionStateProps;

export class ExtractEncryptionSectionComponent extends React.PureComponent<ExtractEncryptionSectionProps, ExtractEncryptionSectionState> {

  public constructor(props: ExtractEncryptionSectionProps) {
    super(props);
    this.state = {
      isChecked: this.isEncryptionEnabled(),
      isRotating: false,
      isSavingSettings: false,
      selectedEncryptionType: undefined,
      hasError: false
    };
  }


  public componentDidMount(): void {
    this.props.retrieveSiteSettings(this.props.siteId);
    this.props.retrieveExtractEncryptionKey();
  }

  public componentDidUpdate(prevProps: ExtractEncryptionSectionProps, prevState: ExtractEncryptionSectionState): void {
    if(prevProps.encryptionKey !== this.props.encryptionKey || prevProps.siteSettings !== this.props.siteSettings) {
      this.setState({
        isChecked: this.isEncryptionEnabled(),
        selectedEncryptionType: this.isKMSKeyGenerated(this.props.encryptionKey) ? EncryptionType.KMS : EncryptionType.CMEK,
      });
    }
  }

  public render() {
    if(!(this.props?.siteSettings?.siteAdminSettings as ISiteAdminSettings)?.cmekAvailable) {
      return null;
    }

    return (
      <div className={extractEncryptionSpaceContainer}>
        <div className={extractEncryptionSettingsContainer}>
          <div className={SettingsStyles.settingsGroupName}>
            {Messages.kmsInOnlineTitle()}
          </div>
          <div style={{marginLeft: '25px'}}>
          {this.getEnableExtractEncryptionSection()}
          {this.getEncryptionTypeSection()}
          {this.state.selectedEncryptionType === EncryptionType.CMEK ? this.getCMEKSaveSection() : null}
          {this.getGenerateKeySection()}
          {this.getEncryptionKeyTable()}
        </div>
        </div>
    </div>
    );
  }

  private getEnableExtractEncryptionSection() {
    return (
    <div>
        <div style={{paddingBottom: '12px'}}>
            {Messages.extractEncyrptionLearnMore()}
        </div>
        <div style={{paddingBottom: '12px'}}>
          <CheckBoxWithLabelWidget
            testId='extract-encrpytion-checkbox'
            label={Messages.extractEncryptionCheckboxLabel()}
            checked={this.isEncryptionEnabled() || this.state.isChecked }
            handleChange={() => this.setState({isChecked: !this.state.isChecked, selectedEncryptionType: undefined})}
            tabIndex={-1}
            removeLeftMargin={true}
            disabled={this.props.isLoading || this.isEncryptionEnabled() }
            />
        </div>
    </div>);
  }

  private getEncryptionTypeSection(){
    return(this.state.isChecked ?
    <div>
      <p>{Messages.extractEncryption_DescriptionSection()}</p>
      <RadioButtonWithLabelGroupWidget
          buttonList={this.getEncrpytionTypeButtons()}
          checkedIndex={this.state.selectedEncryptionType}
          handleChange={(_: string, checkedIndex: number) => {
            this.setState({
              selectedEncryptionType: checkedIndex,
            });
          }}
          aria-labelledby='button'
          testId='extract-encryption-type-radio-button'
          layout={RadioLayoutType.Veritical}
          style={{marginBottom: '12px'}}
          />
      </div> : null);
  }

  private getCMEKSaveSection() {
    return(
      // Only show the save section if the user has checked enable but has not saved yet
      this.state.isChecked && !this.isEncryptionEnabled() ?
      <div>
          <InlineAlertWidget style={{marginBottom: '12px', width: '350px'}} children={Messages.extractEncrpytionEnableWarning()} testId={'enable-extract-encrpytion-alert'} type={InlineAlertType.Warning} />
          <ButtonWidget
              buttonType={ButtonType.Outline}
              testId='cancel-extract-encryption-settings'
              onClick={() => this.setState({isChecked: !this.state.isChecked})}
              children={Messages.cancel()}
              style={{marginRight: '16px'}}
              />
          <ButtonWidget
              buttonType={ButtonType.Primary}
              testId='save-extract-encryption-settings'
              onClick={() => this.updateSiteSettingsForSiteAdminExtractEncrpytion()}
              children={Messages.extractEncryption_enableEncryption_button()}
              disabled={this.state.isSavingSettings}
              showSpinner={this.state.isSavingSettings}
              />
      </div> : null
    );
  }

  private getGenerateKeySection(){
      return (
        // The user has already saved CMEK encryption at this point, or the user has selected KMS encryption
        this.isEncryptionEnabled() || this.state.selectedEncryptionType === EncryptionType.KMS ?
            <ButtonWidget
              buttonType={ButtonType.Outline}
              testId='extract-encrpytion-generate-key'
              onClick={() => this.state.selectedEncryptionType === EncryptionType.CMEK ? this.handleGenerateCMEKKey() : this.handleGenerateKMSKey()}
              disabled={this.state.isRotating}
              children={Messages.extractEncryptionTOLKeyGenerateKeyPrompt()}
              showSpinner={this.state.isRotating}
            /> : null);
    }

  private getEncryptionKeyTable() {
    if(!this.isEncryptionEnabled()) return null;

    if(this.isKMSKeyGenerated(this.props.encryptionKey)) {
      return <KMSEncryptionKeyTable siteSettings={this.props.siteSettings}/>;
    }

    return <CMEKEncryptionKeyTable/>;
  }

  private getEncrpytionTypeButtons(): RadioButtonWithLabelOption<string | undefined>[] {
    return [
      {
        label:
          <span>
            {Messages.extractEncrpytionCMEKOption()}
            <TextLinkWidget
                testId={'cmek-learn-more'}
                target={'_blank'}
                href={HelpService.getHelpUrlByCategory('online-kms-salesforce')}
                className={learnMoreStyle}>
                {Messages.learnMore()}
            </TextLinkWidget>
          </span>,
        testId: 'cmek-encryption-option',
        value: EncryptionType.CMEK.toString(),
        disabled: this.isKMSKeyGenerated(this.props.encryptionKey)
      },
      {
        label:
          <span>
            {Messages.extractEncryptionBYOKMSOption()}
            <TextLinkWidget
                testId={'kms-external-learn-more'}
                target={'_blank'}
                href={HelpService.getHelpUrlByCategory('online-kms-external')}
                className={learnMoreStyle}>
                {Messages.learnMore()}
            </TextLinkWidget>
          </span>,
        testId: 'kms-encryption-option',
        value: EncryptionType.KMS.toString(),
      },
    ];
}

  private isEncryptionEnabled(): boolean {
    return (this.props?.siteSettings?.siteAdminSettings as ISiteAdminSettings)?.extractEncryptionMode === ExtractEncryptionMode.ENFORCED || this.isKMSKeyGenerated(this.props.encryptionKey);
  }

   private updateSiteSettingsForSiteAdminExtractEncrpytion() {
      const request: UpdateSiteSettingsForSiteAdminRequest = getSetEncryptionModeEnforcedRequest(this.props.siteSettings);

        this.setState({isSavingSettings: true}, () => updateSiteSettings(request)
        .then((result: IUpdateSiteSettingsForSiteAdminResult) => {
            if (result.errors) {
                ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
            } else {
                ToasterService.instance.pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });
                this.props.updateSiteSettings(result as ISiteSettingsForSiteAdmin);
                this.props.retrieveExtractEncryptionKey();
            }
        }).catch(e => {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
        }).finally(() => {
          this.setState({isSavingSettings: false});
        }));
    }


    private handleGenerateCMEKKey(): void {
      this.setState({isRotating: true}, () => {
        rotateSiteEncryptionKey()
        .then(() => {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.extractEncryptionCMEK_GenerateKey_SuccessToast() });
        })
        .catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryptionCMEK_GenerateKey_FailureToast() });
        })
        .finally(() => {
            this.setState({isRotating: false});
            this.props.retrieveExtractEncryptionKey();
        });
      });
    }

    private handleGenerateKMSKey(): void {
      // If encryption is enabled and no KMS key is generated, customer is migrating from CMEK to KMS
      if(this.isEncryptionEnabled() && !this.isKMSKeyGenerated(this.props.encryptionKey)) {
        openKMSKeyMigrationDialog();
      }else if(this.isEncryptionEnabled() && this.isKMSKeyActive(this.props.encryptionKey)){
        openReplaceActiveKMSKeyDialog();
      }else{
        openGenerateKMSKeyDialog(this.isEncryptionEnabled(), () => this.onCancelGenerateKMSKey());
      }
    }

    private onCancelGenerateKMSKey(): void {
      // If user hasn't previously created a KMS key, reset the state
      if(!this.isEncryptionEnabled()) {
        this.setState({isChecked: false, selectedEncryptionType: undefined});
      }
    }

    private isKMSKeyGenerated(key: IGetSiteEncryptionKeyResult): boolean {
      return key?.rootKeys?.length > 0;
    }

    private isKMSKeyActive(key: IGetSiteEncryptionKeyResult): boolean {
      return key?.rootKeys?.some(key => key.keyStatus === 'ACTIVE');
    }
}

const mapDispatchToProps: MapDispatchToProps<ExtractEncryptionSectionDispatchProps> =
  (dispatch) => bindActionCreators({
    retrieveExtractEncryptionKey: getExtractEncryptionKeyRequest,
    retrieveSiteSettings: getSiteSettingsRequest,
    updateSiteSettings: siteSettingsRequestSucceeded
  },
  dispatch);

const mapStateToProps: MapStateToProps<ExtractEncryptionSectionStateProps> = (state: AppState) => ({
  isLoading: state.extractEncryptionInfo.isFetchingEncryptionKey || state.extractEncryptionInfo.isFetchingSiteSettings,
  encryptionKey: state.extractEncryptionInfo.encryptionKey,
  siteSettings: state.extractEncryptionInfo.siteSettings
});

export const ExtractEncryptionSection: React.ComponentClass<ExtractEncryptionSectionOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(ExtractEncryptionSectionComponent);